/**
 * ExperienceDetail.jsx
 * 
 * Public page for viewing a marketplace experience/service.
 * Shows full details and allows booking.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  MapPin, 
  Star, 
  Clock, 
  Users,
  ChevronLeft,
  ChevronRight,
  Globe,
  Loader2,
  Check,
  Calendar,
  User,
  Share2,
  ArrowRight,
  MessageCircle,
  Navigation,
  CalendarRange,
  X,
  LayoutGrid,
  Instagram
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import CustomerSignInModal from '../components/CustomerSignInModal';
import { ExperienceCard, ReviewCard, RatingStack } from '../components/marketplace';
import { marketplaceBookingParams } from '../utils/marketplaceLocale';
import ItineraryButton from '../components/ItineraryButton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// SWR fetcher with error handling
const fetcher = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      const error = new Error(res.status === 404 ? 'Experience not found' : 'Failed to load experience');
      error.status = res.status;
      throw error;
    }
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  } catch (err) {
    // Re-throw if it's already a proper error
    if (err.status) throw err;
    // Create a new error for other cases
    const error = new Error('Failed to load experience');
    error.status = 500;
    throw error;
  }
};

const ExperienceDetail = () => {
  const { serviceId, orgSlug: brandSlug } = useParams();
  // Hosted brand-page mode (/b/{orgSlug}/e/{serviceId}) — same component as the
  // marketplace detail page, scoped to one operator's own page
  const brandMode = !!brandSlug;
  const brandHome = brandMode ? `/b/${brandSlug}` : '/marketplace';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const { customer, isAuthenticated } = useCustomerAuth();
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [widgetLoaded, setWidgetLoaded] = useState(false);

  // Listen for widget ready message from iframe + fallback timeout
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'WIDGET_READY') {
        setWidgetLoaded(true);
      }
    };
    window.addEventListener('message', handleMessage);
    
    // Fallback: If postMessage fails, show widget after 5 seconds
    const fallbackTimer = setTimeout(() => {
      setWidgetLoaded(true);
    }, 5000);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(fallbackTimer);
    };
  }, []);

  // SWR for service data - cached and revalidates in background
  const { data: service, error: swrError, isLoading: loading } = useSWR(
    serviceId ? `${API_URL}/api/marketplace/services/${serviceId}${brandMode ? `?brand=${encodeURIComponent(brandSlug)}` : ''}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 30000, // 30 seconds
      errorRetryCount: 2,
      onSuccess: (data) => {
        setCurrentImageIndex(data.mainImageIndex || 0);
        setWidgetLoaded(false); // Reset widget loaded state when service changes
        // GA4 + Meta: fire view_item when service data loads
        import('../utils/analytics').then(({ trackServiceView, initMetaPixel }) => {
          initMetaPixel();
          trackServiceView({
            serviceId: data.id,
            serviceName: data.name,
            price: data.priceFrom || data.price || 0,
            currency: data.currency || 'USD',
            orgName: data.operator?.name || data.organizationName || '',
          });
        });
        // Marketplace funnel beacon (cross-org intelligence)
        import('../utils/marketplaceLocale').then(({ captureMarketplaceAttribution, getMarketplaceCountry }) => {
          captureMarketplaceAttribution();
          import('../utils/bfWidgetTrack').then(({ bfTrack }) => {
            bfTrack('bf-marketplace', 'service_view', {
              serviceId: data.id,
              serviceName: data.name,
              category: data.category,
              country: getMarketplaceCountry(),
              value: data.priceFrom || data.price || 0,
            });
          });
        });
      }
    }
  );

  // SWR for reviews — paginated via "Show more"
  const [reviewLimit, setReviewLimit] = useState(6);
  const { data: reviewsData } = useSWR(
    serviceId ? `${API_URL}/api/v2/public/services/${serviceId}/reviews?limit=${reviewLimit}` : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  // SWR for similar experiences (marketplace only — brand pages stay on-brand)
  const { data: similarData } = useSWR(
    serviceId && !brandMode ? `${API_URL}/api/marketplace/services/${serviceId}/similar?limit=4` : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  const error = swrError?.message || null;

  const handleBookNow = () => {
    if (service?.organizationSlug) {
      const refParam = refCode ? `&ref=${refCode}` : '';
      // Tag the booking flow as marketplace-originated (BM- code + utm snapshot)
      import('../utils/analytics').then(({ trackCheckoutStarted }) => {
        trackCheckoutStarted({
          serviceId: service.id,
          serviceName: service.name,
          price: service.priceFrom || service.price || 0,
          currency: service.currency || 'USD',
          orgName: service.operator?.name || '',
        });
      });
      import('../utils/bfWidgetTrack').then(({ bfTrack }) => {
        bfTrack('bf-marketplace', 'checkout_started', {
          serviceId: service.id,
          value: service.priceFrom || service.price || 0,
        });
      });
      window.open(`/book?org=${service.organizationSlug}&service=${service.id}${refParam}&${marketplaceBookingParams()}`, '_blank');
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: service.name,
        text: service.shortDescription || service.tagline,
        url: window.location.href
      });
    } catch (error) {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDuration = (minutes, isMultiDay, days) => {
    if (isMultiDay && days) {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours} hr ${mins} min` : `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  // Combined gallery — operator photos first, then guest (customer review) photos.
  // Hero carousel rotates through OPERATOR photos only; tiles + lightbox show all.
  const opImages = service?.images || [];
  const guestPhotos = service?.customerPhotos || []; // [{url, customerName}]
  const allImages = [...opImages, ...guestPhotos.map((p) => p.url)];
  const heroImages = opImages.length > 0 ? opImages : allImages;

  const nextImage = () => {
    if (heroImages.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }
  };

  // Auto-rotate the main carousel every 10s (paused while the lightbox is open)
  useEffect(() => {
    const count = heroImages.length;
    if (count < 2 || galleryOpen) return;
    const id = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % count);
    }, 10000);
    return () => clearInterval(id);
  }, [heroImages.length, galleryOpen]);

  const openGallery = (idx) => {
    setGalleryIndex(idx);
    setGalleryOpen(true);
  };

  const prevImage = () => {
    if (heroImages.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + heroImages.length) % heroImages.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md pt-[env(safe-area-inset-top)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16">
              <Link to="/marketplace" className="flex items-center gap-2">
                <img 
                  src="/logo-icon.png" 
                  alt="BookingFlows" 
                  className="h-9 w-9 object-contain rounded-lg"
                />
                <span className="text-xl font-bold text-gray-900">BookingFlows</span>
              </Link>
            </div>
          </div>
        </nav>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <Globe className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{error}</h1>
          <p className="text-gray-600 mb-6">The experience you&apos;re looking for may no longer be available</p>
          <Button onClick={() => navigate(brandMode ? brandHome : '/marketplace')} variant="outline" className="rounded-full">
            <ChevronLeft className="w-4 h-4 mr-2" />
            {brandMode ? 'Back to all experiences' : 'Back to marketplace'}
          </Button>
        </div>
      </div>
    );
  }

  const images = allImages;
  const operator = service?.operator;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => {
                  // Navigate to logical previous page instead of browser history
                  // (avoids going back to Stripe after checkout)
                  if (brandMode) {
                    navigate(brandHome);
                  } else if (service?.city && service?.category) {
                    navigate(`/search?city=${encodeURIComponent(service.city)}&category=${encodeURIComponent(service.category)}`);
                  } else if (service?.city) {
                    navigate(`/search?city=${encodeURIComponent(service.city)}`);
                  } else {
                    navigate('/marketplace');
                  }
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              {brandMode ? (
                <Link to={brandHome} className="flex items-center gap-2" data-testid="brand-nav-home">
                  {operator?.logoUrl ? (
                    <img src={operator.logoUrl} alt={operator.name} className="h-9 w-9 object-contain rounded-lg bg-white" />
                  ) : (
                    <div className="h-9 w-9 rounded-lg bg-[#04091B] flex items-center justify-center">
                      <span className="text-white font-semibold">{operator?.name?.charAt(0) || 'B'}</span>
                    </div>
                  )}
                  <span className="text-xl font-bold text-gray-900 hidden sm:inline">{operator?.name}</span>
                </Link>
              ) : (
                <Link to="/marketplace" className="flex items-center gap-2">
                  <img 
                    src="/logo-icon.png" 
                    alt="BookingFlows" 
                    className="h-9 w-9 object-contain rounded-lg"
                  />
                  <span className="text-xl font-bold text-gray-900 hidden sm:inline">BookingFlows</span>
                </Link>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShare}
                className="gap-2 rounded-full"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </Button>
              {brandMode ? null : isAuthenticated ? (
                <Link to="/my-trips">
                  <div className="flex items-center gap-2 px-2 py-1 rounded-full border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
                    {customer?.avatar_url ? (
                      <img 
                        src={customer.avatar_url} 
                        alt={customer.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                    )}
                  </div>
                </Link>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full"
                  onClick={() => setShowSignInModal(true)}
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Breadcrumb Trail - Inline, single line */}
            <p className="text-sm mb-4">
              <Link to={brandHome} className="text-gray-500 hover:text-blue-600 transition-colors">Home</Link>
              <span className="text-gray-300 mx-2">&gt;</span>
              {!brandMode && service.city && (
                <span className="hidden sm:inline">
                  <Link 
                    to={`/search?city=${encodeURIComponent(service.city)}`} 
                    className="text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    {service.city}
                  </Link>
                  <span className="text-gray-300 mx-2">&gt;</span>
                </span>
              )}
              {service.category && !brandMode && (
                <span className="hidden md:inline">
                  <Link 
                    to={`/search?city=${encodeURIComponent(service.city || '')}&category=${encodeURIComponent(service.category)}`} 
                    className="text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    {service.category}
                  </Link>
                  <span className="text-gray-300 mx-2">&gt;</span>
                </span>
              )}
              <span className="text-gray-900 font-medium">{service.name}</span>
            </p>

            {/* Image Gallery — 1: single · 2: 50/50 split · 3+: big left + 2 stacked right (grouped corner rounding) */}
            <div className={images.length >= 2 ? `grid gap-2 sm:gap-3 ${images.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}` : ''}>
              <div
                className={`relative overflow-hidden bg-gray-100 shadow-[0_8px_24px_rgba(0,0,0,0.05)] aspect-[16/10] ${
                  images.length >= 2
                    ? `cursor-pointer rounded-3xl sm:rounded-tr-lg sm:rounded-br-lg ${images.length === 2 ? 'col-span-2 sm:col-span-1' : 'col-span-3 sm:col-span-2'}`
                    : 'rounded-3xl'
                }`}
                onClick={images.length >= 2 ? () => openGallery(currentImageIndex) : undefined}
                data-testid="gallery-main"
              >
                {images.length > 0 && heroImages[currentImageIndex] ? (
                  <>
                    <img
                      src={heroImages[currentImageIndex]}
                      alt={service.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to placeholder on broken image
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.parentElement.querySelector('.image-fallback')?.classList.remove('hidden');
                      }}
                    />
                    {/* Hidden fallback - shown if image fails */}
                    <div className="image-fallback hidden absolute inset-0 flex items-center justify-center bg-gray-100">
                      <Globe className="w-20 h-20 text-blue-300" />
                    </div>
                    {heroImages.length > 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); prevImage(); }}
                          data-testid="gallery-prev"
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full shadow-md hover:bg-white transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); nextImage(); }}
                          data-testid="gallery-next"
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full shadow-md hover:bg-white transition-colors"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {heroImages.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                              className={`w-1.5 h-1.5 rounded-full transition-colors ${
                                idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                    {images.length > 1 && (
                      <>
                        {/* Mobile: see-all-photos chip (side tiles are hidden on mobile) */}
                        <button
                          onClick={(e) => { e.stopPropagation(); openGallery(currentImageIndex); }}
                          className="sm:hidden absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-md"
                          data-testid="gallery-see-all-mobile"
                        >
                          <LayoutGrid className="w-3.5 h-3.5" />
                          {images.length}
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <Globe className="w-20 h-20 text-blue-300" />
                  </div>
                )}
              </div>

              {/* Right side — 2 photos: equal 50/50 tile · 3+: two stacked tiles, right corners rounded */}
              {images.length === 2 && (
                <button
                  onClick={() => openGallery(1)}
                  className="hidden sm:block relative overflow-hidden bg-gray-100 group/tile focus:outline-none aspect-[16/10] rounded-lg rounded-tr-3xl rounded-br-3xl"
                  data-testid="gallery-tile-1"
                >
                  <img
                    src={images[1]}
                    alt={`${service.name} photo 2`}
                    className="absolute inset-0 w-full h-full object-cover group-hover/tile:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </button>
              )}
              {images.length >= 3 && (
                <div className="hidden sm:grid col-span-1 gap-2 sm:gap-3 grid-rows-2 relative">
                  {[1, 2].map((tileIdx, i) => (
                    <button
                      key={tileIdx}
                      onClick={() => openGallery(tileIdx)}
                      className={`relative overflow-hidden bg-gray-100 group/tile focus:outline-none rounded-lg ${i === 0 ? 'rounded-tr-3xl' : 'rounded-br-3xl'}`}
                      data-testid={`gallery-tile-${tileIdx}`}
                    >
                      <img
                        src={images[tileIdx]}
                        alt={`${service.name} photo ${tileIdx + 1}`}
                        className="absolute inset-0 w-full h-full object-cover group-hover/tile:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </button>
                  ))}
                  {images.length > 3 && (
                    <button
                      onClick={() => openGallery(0)}
                      className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3.5 py-2 text-xs font-semibold text-gray-900 shadow-md hover:bg-white transition-colors"
                      data-testid="gallery-view-all-btn"
                    >
                      <LayoutGrid className="w-3.5 h-3.5" />
                      View all images
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Photo lightbox — browse every photo */}
            <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
              <DialogContent
                className="max-w-5xl w-[96vw] p-0 bg-black border-0 rounded-2xl overflow-hidden [&>button]:hidden"
                data-testid="gallery-lightbox"
              >
                <DialogTitle className="sr-only">{service.name} photos</DialogTitle>
                <div className="relative">
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white/80 text-sm font-medium" data-testid="gallery-lightbox-counter">
                        {galleryIndex + 1} / {images.length}
                      </span>
                      {galleryIndex >= opImages.length && (
                        <span className="px-2 py-0.5 rounded-full bg-white/15 text-white/90 text-[11px] font-medium" data-testid="gallery-guest-photo-chip">
                          Guest photo{guestPhotos[galleryIndex - opImages.length]?.customerName ? ` · ${guestPhotos[galleryIndex - opImages.length].customerName}` : ''}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setGalleryOpen(false)}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                      data-testid="gallery-lightbox-close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="relative flex items-center justify-center h-[62vh] px-4">
                    <img
                      src={images[galleryIndex]}
                      alt={`${service.name} photo ${galleryIndex + 1}`}
                      className="max-h-full max-w-full object-contain rounded-lg"
                    />
                    {images.length > 1 && (
                      <>
                        <button
                          onClick={() => setGalleryIndex((galleryIndex - 1 + images.length) % images.length)}
                          className="absolute left-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                          data-testid="gallery-lightbox-prev"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setGalleryIndex((galleryIndex + 1) % images.length)}
                          className="absolute right-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                          data-testid="gallery-lightbox-next"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>
                  {images.length > 1 && (
                    <div className="flex gap-2 px-4 py-4 overflow-x-auto">
                      {images.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setGalleryIndex(idx)}
                          className={`relative shrink-0 w-16 h-12 rounded-lg overflow-hidden transition-opacity ${
                            idx === galleryIndex ? 'ring-2 ring-white opacity-100' : 'opacity-50 hover:opacity-80'
                          }`}
                          data-testid={`gallery-lightbox-thumb-${idx}`}
                        >
                          <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Title & Quick Info */}
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{service.name}</h1>
              
              {/* Rating — BookingFlows + Google, clearly branded */}
              <div className="mb-4">
                <RatingStack
                  bfRating={reviewsData?.average_rating ?? operator?.rating}
                  bfCount={reviewsData?.total ?? service.reviewCount}
                  googleReviews={operator?.googleReviews}
                  bfTestId="title-rating-btn"
                  onBfClick={() => document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' })}
                />
                {!(reviewsData?.total ?? service.reviewCount) && !operator?.googleReviews?.count && (
                  <p className="text-sm text-gray-400">No reviews yet</p>
                )}
              </div>
              
              {/* Quick Stats - Prominent location display */}
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-base sm:text-lg text-gray-700">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">{service.city || 'Location TBD'}</span>
                </div>
                <span className="text-gray-300 text-2xl">·</span>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <span>{formatDuration(service.durationMinutes, service.isMultiDay, service.durationDays)}</span>
                </div>
                <span className="text-gray-300 text-2xl">·</span>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-400" />
                  <span>Up to {service.maxCapacity} guests</span>
                </div>
              </div>
            </div>

            {/* Operator Info - Simplified */}
            {operator && (
              <div
                onClick={() => navigate(brandMode ? brandHome : `/host/${operator.id}`)}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(brandMode ? brandHome : `/host/${operator.id}`); }}
                className="block p-4 border border-gray-200 rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 group mt-4 cursor-pointer"
                data-testid="hosted-by-link"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 p-1">
                    {operator.logoUrl ? (
                      <img src={operator.logoUrl} alt={operator.name} className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full bg-[#04091B] rounded flex items-center justify-center">
                        <span className="text-white font-semibold text-lg">{operator.name?.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3">
                      <p className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                        Hosted by {operator.name}
                      </p>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Verified
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {operator.serviceCount || 1} experience{(operator.serviceCount || 1) !== 1 ? 's' : ''}
                    </p>
                    {(operator.socialLinks?.instagram || operator.website) && (
                      <div className="flex items-center gap-4 mt-2">
                        {operator.socialLinks?.instagram && (
                          <a
                            href={operator.socialLinks.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-pink-600 hover:text-pink-700 transition-colors"
                            data-testid="host-instagram-link"
                          >
                            <Instagram className="w-3.5 h-3.5" />
                            Instagram
                          </a>
                        )}
                        {operator.website && (
                          <a
                            href={operator.website.startsWith('http') ? operator.website : `https://${operator.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                            data-testid="host-website-link"
                          >
                            <Globe className="w-3.5 h-3.5" />
                            Website
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
                </div>
              </div>
            )}

            {/* Description */}
            {service.description && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">About this experience</h2>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">{service.description}</p>
                </div>
              </div>
            )}

            {/* What's Included / Not Included */}
            {((service.whatsIncluded && service.whatsIncluded.length > 0) || (service.notIncluded && service.notIncluded.length > 0)) && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">What&apos;s included</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {(service.whatsIncluded || []).length > 0 && (
                    <ul className="space-y-3">
                      {service.whatsIncluded.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {(service.notIncluded || []).length > 0 && (
                    <div data-testid="not-included-section">
                      <p className="text-sm font-semibold text-gray-500 mb-2 sm:hidden">Not included</p>
                      <ul className="space-y-3">
                        {service.notIncluded.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-500">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Good To Know — quick-glance chip cards */}
            {(service.goodToKnow || []).filter(g => g && g.title).length > 0 && (
              <div data-testid="good-to-know-section">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Good to know</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {service.goodToKnow.filter(g => g && g.title).map((g, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-2xl p-4">
                      <p className="text-sm font-semibold text-gray-800 mb-2">{g.title}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(g.chips || []).map((c, ci) => (
                          <span key={ci} className="bg-white shadow-[0_1px_4px_rgba(0,0,0,0.06)] rounded-full px-3 py-1 text-xs text-gray-600">{c}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Sections */}
            {service.customSections && service.customSections.length > 0 && (
              service.customSections.map((section, idx) => (
                <div key={idx}>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">{section.title}</h2>
                  <p className="text-gray-600 whitespace-pre-line">{section.content}</p>
                </div>
              ))
            )}

            {/* Meeting Location */}
            <div className="bg-blue-50 rounded-2xl p-5">
              <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-600" />
                Meeting Location
              </h2>
              {(service.mapLocation?.lat && service.mapLocation?.lng) ? (
                <div className="mb-4" data-testid="location-map">
                  <iframe
                    title="Meeting location map"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${(service.mapLocation.lng - 0.02).toFixed(5)}%2C${(service.mapLocation.lat - 0.012).toFixed(5)}%2C${(service.mapLocation.lng + 0.02).toFixed(5)}%2C${(service.mapLocation.lat + 0.012).toFixed(5)}&layer=mapnik&marker=${service.mapLocation.lat}%2C${service.mapLocation.lng}`}
                    className="w-full h-56 rounded-xl border-0"
                    loading="lazy"
                  />
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${service.mapLocation.lat},${service.mapLocation.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
                    data-testid="directions-btn"
                  >
                    <Navigation className="w-4 h-4" />
                    Directions
                  </a>
                </div>
              ) : null}
              {service.locationDetails ? (
                <div className="text-gray-600">
                  <p className="mb-2">{service.locationDetails}</p>
                  {service.city && (
                    <p className="text-sm text-gray-500">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      {service.city}{service.state ? `, ${service.state}` : ''}{service.country ? `, ${service.country}` : ''}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-gray-600">
                  <p className="mb-2">Exact meeting location will be provided after booking.</p>
                  {service.city && (
                    <p className="text-sm text-gray-500">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      {service.city}{service.state ? `, ${service.state}` : ''}{service.country ? `, ${service.country}` : ''}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Cancellation Policy */}
            {service.customCancellationPolicy ? (
              <div className="bg-gray-50 rounded-2xl p-5">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-600" />
                  Cancellation Policy
                </h2>
                <p className="text-gray-600">{service.customCancellationPolicy}</p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-2xl p-5">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Check className="w-5 h-5 text-gray-600" />
                  Cancellation Policy
                </h2>
                <p className="text-gray-600">Free cancellation up to 24 hours before the experience. Full refund if cancelled within the allowed window.</p>
              </div>
            )}

          </div>

          {/* Booking Widget - Clean, No Wrapper */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 p-1 relative">
              {/* Skeleton Loader - Shows while iframe loads */}
              {!widgetLoaded && (
                <div className="absolute inset-0 z-10 bg-white rounded-2xl p-4 animate-pulse">
                  {/* Header skeleton */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-5 bg-gray-200 rounded w-32" />
                    <div className="h-4 bg-gray-200 rounded w-24" />
                  </div>
                  
                  {/* Calendar header skeleton */}
                  <div className="flex items-center justify-between mb-3 px-2">
                    <div className="h-4 w-4 bg-gray-200 rounded" />
                    <div className="h-5 bg-gray-200 rounded w-28" />
                    <div className="h-4 w-4 bg-gray-200 rounded" />
                  </div>
                  
                  {/* Calendar grid skeleton */}
                  <div className="bg-gray-50 rounded-2xl p-3 mb-4">
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['S','M','T','W','T','F','S'].map((d, i) => (
                        <div key={i} className="h-4 bg-gray-200 rounded mx-auto w-6" />
                      ))}
                    </div>
                    {/* Calendar days */}
                    {[...Array(5)].map((_, row) => (
                      <div key={row} className="grid grid-cols-7 gap-1 mb-1">
                        {[...Array(7)].map((_, col) => (
                          <div 
                            key={col} 
                            className="aspect-square rounded-lg bg-gray-200 animate-pulse"
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                  
                  {/* Service info skeleton */}
                  <div className="space-y-3 pt-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                    <div className="h-10 bg-gray-200 rounded-lg w-full mt-4" />
                  </div>
                </div>
              )}
              
              <iframe
                src={`/services-grid.html?org=${service.organizationSlug}&service=${service.id}&marketplace=true&embed=true&${marketplaceBookingParams()}`}
                title="Book this experience"
                className={`w-full border-0 transition-opacity duration-300 ${widgetLoaded ? 'opacity-100' : 'opacity-0'}`}
                style={{ 
                  height: '850px',
                  minHeight: '850px',
                  background: 'transparent'
                }}
                data-testid="booking-widget-iframe"
              />
            </div>
          </div>
        </div>

        {/* Reviews — flat, full-width, just above "You might also like" */}
        {reviewsData && reviewsData.total > 0 && (
          <div id="reviews-section" className="mt-12 scroll-mt-20" data-testid="reviews-section">
            <div className="flex items-end justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Reviews <span className="text-gray-400 font-normal text-lg">({reviewsData.total})</span>
              </h2>
              <div className="flex items-center gap-1.5">
                <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                <span className="text-xl font-bold text-gray-900">{reviewsData.average_rating}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {reviewsData.reviews.map((review) => (
                <div key={review.id} className="bg-gray-50 rounded-3xl p-5" data-testid={`review-card-${review.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      {review.customer_avatar ? (
                        <img src={review.customer_avatar} alt={review.customer_name} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center">
                          <span className="text-white font-medium text-sm">{review.customer_name?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{review.customer_name}</p>
                        <p className="text-xs text-gray-400">
                          {review.created_at ? new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                      ))}
                    </div>
                  </div>
                  {review.title && <p className="font-medium text-gray-900 mb-1">{review.title}</p>}
                  {review.comment && <p className="text-gray-600 text-sm leading-relaxed">{review.comment}</p>}
                  {review.photos?.length > 0 && (
                    <div className="flex gap-2 mt-3">
                      {review.photos.map((p, i) => (
                        <img key={i} src={p} alt="Review" className="w-20 h-20 rounded-2xl object-cover" data-testid="review-photo" />
                      ))}
                    </div>
                  )}
                  {review.owner_response && (
                    <div className="mt-3 bg-blue-50 rounded-2xl p-3">
                      <p className="text-xs font-medium text-blue-700 mb-1">Response from operator</p>
                      <p className="text-sm text-gray-600">{review.owner_response}</p>
                    </div>
                  )}
                  {review.is_verified && (
                    <p className="text-xs text-blue-600 flex items-center gap-1 mt-3">
                      <Check className="w-3 h-3" /> Verified trip
                    </p>
                  )}
                </div>
              ))}
            </div>
            {reviewsData.has_more && (
              <div className="text-center mt-6">
                <button
                  onClick={() => setReviewLimit((l) => l + 6)}
                  data-testid="show-more-reviews-btn"
                  className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Show more reviews
                </button>
              </div>
            )}
          </div>
        )}

        {/* Similar Experiences Section - Using shared ExperienceCard */}
        {similarData && similarData.services && similarData.services.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">You might also like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {similarData.services.map((svc) => (
                <ExperienceCard key={svc.id} service={svc} />
              ))}
            </div>
          </div>
        )}

        {/* Full-Width CTA Section - Operator focused, at the end (marketplace only) */}
        {!brandMode && (
        <div className="mt-12">
          <div 
            className="bg-[#04091B] rounded-3xl p-8 text-white text-center"
          >
            <h3 className="text-xl font-semibold mb-2">Want to add your experience?</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Join thousands of operators and start earning by listing your tours, activities, and experiences on BookingFlows.
            </p>
            <Link to="/for-suppliers">
              <Button variant="secondary" className="bg-white text-gray-900 hover:bg-gray-100 rounded-full px-8">
                List Your Business <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
        )}
      </div>

      {/* Footer */}
      {brandMode ? (
        <footer className="mt-16 py-10 px-4 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm font-semibold text-gray-900">{operator?.name}</p>
            <a href="https://bookingflows.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
              Powered by <span className="font-semibold">BookingFlows</span>
            </a>
          </div>
        </footer>
      ) : (
      <footer className="mt-16 py-12 px-4 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <img 
              src="/logo-icon.png" 
              alt="BookingFlows" 
              className="h-8 w-8 object-contain rounded-lg"
            />
            <span className="text-lg font-bold text-gray-900">BookingFlows</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
            <Link to="/marketplace" className="hover:text-blue-600 transition-colors">Home</Link>
            <Link to="/search" className="hover:text-blue-600 transition-colors">All Experiences</Link>
            <Link to="/support" className="hover:text-blue-600 transition-colors" data-testid="footer-support-link">Need Help?</Link>
            <Link to="/for-suppliers" className="hover:text-blue-600 transition-colors">For Operators</Link>
            <Link to="/blog" className="hover:text-blue-600 transition-colors">Blog</Link>
            <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link>
          </div>
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} BookingFlows
          </p>
        </div>
      </footer>
      )}

      {/* Customer Sign In Modal */}
      <CustomerSignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal} 
      />

      {/* Mobile Fixed Book Now Button */}
      <div 
        className="fixed bottom-0 left-0 right-0 px-4 py-2 bg-white/95 backdrop-blur-sm border-t border-gray-100 lg:hidden z-50" 
        style={{ 
          boxShadow: '0 -8px 20px -8px rgb(0 0 0 / 0.08)',
          paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))'
        }}
      >
        <div className="flex items-center justify-between gap-4 max-w-lg mx-auto">
          <div className="flex-1">
            <p className="text-lg font-bold text-gray-900">
              {formatPrice(service?.price || 0, service?.currency)}
              <span className="text-sm font-normal text-gray-500 ml-1">/ person</span>
            </p>
          </div>
          <Button 
            onClick={() => {
              const widget = document.querySelector('[data-testid="booking-widget-iframe"]');
              if (widget) {
                widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-semibold"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Book Now
          </Button>
        </div>
      </div>

      {/* Spacer for mobile fixed button */}
      <div className="h-20 lg:hidden" />

      {/* Bottom fade effect for smooth scrolling */}
      <div 
        className="fixed bottom-0 left-0 right-0 h-16 pointer-events-none z-30"
        style={{
          background: 'linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)'
        }}
      />
      <ItineraryButton />
    </div>
  );
};

export default ExperienceDetail;
